import React, { useCallback, useEffect, useMemo, useState } from "react";
import { logError } from "@/lib/logger";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import AvailableNowToggle from "@/components/AvailableNowToggle";
import {
    SquaresFour,
    Shield,
    Briefcase,
    UsersThree,
    SignOut,
    Lightning,
    List,
    X,
    ChartBar,
    ShieldCheck,
    Buildings,
    CalendarBlank,
    Gift,
    CreditCard,
    CurrencyInr,
    ChatCircleDots,
    Bell,
    Gear,
    Envelope,
    ArrowsClockwise,
    ChartLineUp,
    ChartPieSlice,
    PaperPlaneTilt,
    Crown,
    ChartLineUp as MRRIcon,
    ChartLine,
    ToggleLeft as Toggle,
    ClipboardText,
    SealCheck,
    Wrench,
    Sparkle,
    User,
    Clock,
    Cpu,
    BellRinging,
    Broadcast,
    Copy,
    PhoneCall,
    Handshake,
} from "@phosphor-icons/react";
import { useFeatures } from "@/lib/features";
import Brand from "@/components/Brand";
import CommandPalette from "@/components/CommandPalette";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PageTransition from "@/components/PageTransition";
import { InstallAppButton } from "@/components/InstallPrompt";

const NAV = [
    // Superadmin-only cluster — dedicated cockpit, NOT the tenant admin surface.
    { to: "/superadmin",               label: "Workspaces",    icon: Crown,          end: true, testid: "nav-superadmin",           roles: ["superadmin"] },
    { to: "/superadmin/subscriptions", label: "Subscriptions", icon: MRRIcon,                   testid: "nav-superadmin-subs",       roles: ["superadmin"] },
    { to: "/superadmin/features",      label: "Features",      icon: Toggle,                    testid: "nav-superadmin-features",   roles: ["superadmin"] },
    { to: "/revenue",                  label: "Revenue",       icon: CurrencyInr,               testid: "nav-superadmin-revenue",    roles: ["superadmin"] },
    { to: "/superadmin/growth",        label: "Growth",        icon: ChartLine,                 testid: "nav-superadmin-growth",     roles: ["superadmin"] },
    { to: "/superadmin/ai-jobs",       label: "AI jobs",       icon: Cpu,                       testid: "nav-superadmin-ai-jobs",    roles: ["superadmin", "admin"] },
    { to: "/superadmin/push-health",   label: "Push health",   icon: BellRinging,               testid: "nav-superadmin-push-health",roles: ["superadmin", "admin"] },
    { to: "/superadmin/email-campaign", label: "Email campaign", icon: PaperPlaneTilt,           testid: "nav-superadmin-email-campaign", roles: ["superadmin", "admin"] },
    { to: "/superadmin/dropoff",       label: "Drop-off queue",icon: PaperPlaneTilt,            testid: "nav-superadmin-dropoff",    roles: ["superadmin", "admin"] },
    { to: "/admin/email-health",       label: "Email health",  icon: Envelope,                  testid: "nav-superadmin-email-health", roles: ["superadmin"] },
    { to: "/admin/renewal-emails",     label: "Renewal drips", icon: ArrowsClockwise,           testid: "nav-superadmin-renewal-drips", roles: ["superadmin"] },
    { to: "/superadmin/verification",  label: "Verification",  icon: SealCheck,                 testid: "nav-superadmin-verification", roles: ["superadmin"] },
    { to: "/admin/verified-badge",     label: "Verified badges", icon: SealCheck,               testid: "nav-verified-badge",         roles: ["superadmin"] },
    { to: "/superadmin/audit",         label: "Audit log",     icon: ClipboardText,             testid: "nav-superadmin-audit",      roles: ["superadmin"] },
    { to: "/superadmin/data-heal",     label: "Data heal",     icon: Wrench,                    testid: "nav-superadmin-data-heal",  roles: ["superadmin"] },
    { to: "/superadmin/users",         label: "Users search",  icon: User,                      testid: "nav-superadmin-users",      roles: ["superadmin"] },
    { to: "/superadmin/candidate-signals", label: "Candidate signals", icon: Broadcast,             testid: "nav-superadmin-candidate-signals", roles: ["superadmin"] },
    { to: "/superadmin/duplicates",    label: "Duplicate people", icon: Copy,                    testid: "nav-superadmin-duplicates", roles: ["superadmin", "admin"] },
    { to: "/superadmin/schedulers",    label: "Schedulers",    icon: Clock,                     testid: "nav-superadmin-schedulers", roles: ["superadmin"] },
    // V2.0.65 — Partner Waitlist visible to admin too so info@naukridaddy.com
    // (owner-admin, role=admin) can manage partner signups from the left nav.
    { to: "/superadmin/partner-waitlist", label: "Partner waitlist", icon: Handshake,              testid: "nav-partner-waitlist",     roles: ["superadmin", "admin"] },

    // ─── Tenant workspace nav (admin / recruiter / hiring_manager) ────────
    // V1.5.8.24 (Feb 18, 2026) — reordered from random into a
    // workflow-driven sequence. Same order for all 3 roles; each role
    // simply hides the items they lack access to (see `roles`).
    //
    //   A. DAILY WORK       — the pipeline flow
    //   B. COMMS & ACTIONS  — what needs attention
    //   C. GROWTH & INSIGHTS
    //   D. ADMIN CONTROLS   — settings/billing/audit

    // A. Daily work — pipeline flow
    { to: "/dashboard",     label: "Dashboard",     icon: SquaresFour,  end: true, testid: "nav-dashboard",    roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/clients",       label: "Clients",       icon: Buildings,               testid: "nav-clients",      roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/requirements",  label: "Requirements",  icon: Briefcase,               testid: "nav-jobs",         roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/candidates",    label: "Candidates",    icon: UsersThree,              testid: "nav-candidates",   roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/crm",           label: "CRM",           icon: PhoneCall,               testid: "nav-crm",          roles: ["admin", "recruiter", "hiring_manager", "superadmin"] },
    { to: "/marketplace",   label: "Marketplace",   icon: Sparkle,                 testid: "nav-marketplace",  roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/interviews",    label: "Interviews",    icon: CalendarBlank,           testid: "nav-interviews",   roles: ["admin", "recruiter", "hiring_manager"] },

    // B. Comms & actions
    { to: "/nudge-queue",   label: "Nudge queue",   icon: PaperPlaneTilt,          testid: "nav-nudge-queue",  roles: ["admin", "recruiter", "hiring_manager"], nudgeBadge: true },
    { to: "/messages",      label: "Messages",      icon: ChatCircleDots,          testid: "nav-messages",         roles: ["admin", "recruiter", "hiring_manager", "superadmin"], showUnread: true },
    { to: "/notifications", label: "Notifications", icon: Bell,                    testid: "nav-notifications", roles: ["admin", "recruiter", "hiring_manager"], badge: true },
    { to: "/testimonials",  label: "Testimonials",  icon: ChatCircleDots,          testid: "nav-testimonials", roles: ["admin", "hiring_manager"] },
    { to: "/referrals",     label: "Referrals",     icon: Gift,                    testid: "nav-referrals",    roles: ["admin"], feature: "referrals" },

    // C. Growth & insights
    { to: "/analytics",     label: "Analytics",     icon: ChartBar,                testid: "nav-analytics",    roles: ["admin", "recruiter", "hiring_manager"], feature: "advanced_analytics" },
    { to: "/performance",   label: "Performance",   icon: ChartLineUp,             testid: "nav-performance",  roles: ["admin", "recruiter", "hiring_manager"] },
    { to: "/reports",       label: "Reports",       icon: ChartPieSlice,           testid: "nav-reports",      roles: ["admin", "hiring_manager"] },

    // D. Admin controls
    { to: "/users",              label: "Team",           icon: ShieldCheck,   testid: "nav-users",          roles: ["admin", "hiring_manager", "superadmin"] },
    { to: "/billing",            label: "Billing",        icon: CreditCard,    testid: "nav-billing",        roles: ["admin"] },
    { to: "/settings",           label: "Settings",       icon: Gear,          testid: "nav-settings",       roles: ["admin"] },
    { to: "/settings/partner-access", label: "Partner Access", icon: Shield,   testid: "nav-partner-access", roles: ["admin", "superadmin"] },
    { to: "/admin/audit-log",    label: "Audit log",      icon: ClipboardText, testid: "nav-audit-log",      roles: ["admin"] },
    { to: "/admin/parser-cleanup", label: "Parser cleanup", icon: Wrench,      testid: "nav-parser-cleanup", roles: ["admin", "hiring_manager", "superadmin"] },
];

// V1.5.8.27 — tenant nav catalog used by WorkspaceSettings' sidebar
// reorder UI. Kept in this module (single source of truth) so labels stay
// in sync automatically.
export const TENANT_NAV_CATALOG = NAV
    .filter((n) => n.roles.some((r) => ["admin", "recruiter", "hiring_manager"].includes(r)))
    .map((n) => ({ to: n.to, label: n.label, roles: n.roles }));

export default function AppLayout() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [tenant, setTenant] = useState(null); // { name, logo_url, primary_color }

    // Close mobile drawer on route change
    React.useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    // Load the current tenant's branding once we know the user is signed in.
    // Superadmin belongs to the platform (default) workspace — no tenant branding overlay.
    useEffect(() => {
        if (!user || user === false) return;
        if (!["admin", "recruiter", "hiring_manager"].includes(user.role)) return;
        let cancel = false;
        const load = () => {
            api.get("/workspaces/current").then((r) => {
                if (cancel) return;
                const s = r.data?.settings || {};
                setTenant({
                    name: r.data?.name || null,
                    logo_url: s.logo_url || null,
                    primary_color: s.primary_color || null,
                });
            }).catch(() => {});
        };
        load();
        const handler = () => load();
        window.addEventListener("naukri:tenant-branding-changed", handler);
        return () => {
            cancel = true;
            window.removeEventListener("naukri:tenant-branding-changed", handler);
        };
    }, [user]);

    // Global handler for HTTP 402 (plan_limit) — show a friendly upgrade toast.
    // Only admins can access /billing, so non-admins see a "contact admin" message.
    useEffect(() => {
        const onPlanLimit = (e) => {
            const d = e.detail || {};
            const isAdmin = user && user.role === "admin";
            if (isAdmin) {
                toast.error(d.message || "You've hit a plan limit.", {
                    duration: 8000,
                    action: {
                        label: "Upgrade",
                        onClick: () => nav(d.upgrade_url || "/billing"),
                    },
                });
            } else {
                toast.error(
                    (d.message || "You've hit a plan limit.") +
                        " Please contact your workspace admin to upgrade.",
                    { duration: 8000 },
                );
            }
        };
        window.addEventListener("naukri:plan-limit", onPlanLimit);
        return () => window.removeEventListener("naukri:plan-limit", onPlanLimit);
    }, [nav, user]);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            logError("Logout request failed:", e);
        }
        setUser(false);
        nav("/login");
    }, [nav, setUser]);

    // Batch 3b Scale-ready — poll pending interview follow-ups every 60s.
    // Show OVERDUE count in the badge (higher urgency signal) when > 0,
    // otherwise fall back to total 48h pending count.
    const [followupCount, setFollowupCount] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);
    useEffect(() => {
        if (!user || user === false) return;
        if (!["admin", "recruiter", "hiring_manager"].includes(user.role)) return;
        let cancel = false;
        const fetchCount = () =>
            api
                .get("/interviews/upcoming", { params: { hours: 48, limit: 1, offset: 0 } })
                .then((r) => {
                    if (cancel) return;
                    setFollowupCount(r.data.total || 0);
                    setOverdueCount(r.data.overdue_count || 0);
                })
                .catch(() => {});
        fetchCount();
        const t = setInterval(fetchCount, 60000);
        return () => { cancel = true; clearInterval(t); };
    }, [user]);
    const badgeCount = overdueCount > 0 ? overdueCount : followupCount;
    const badgeUrgent = overdueCount > 0;

    // Nudge queue count — polled every 90s. Sum of awaiting-registration
    // and stuck-in-stage buckets (matches the /nudge-queue/summary badge).
    const [nudgeCount, setNudgeCount] = useState(0);
    const [nudgeUrgent, setNudgeUrgent] = useState(false);
    useEffect(() => {
        if (!user || user === false) return;
        if (!["admin", "recruiter", "hiring_manager"].includes(user.role)) return;
        let cancel = false;
        const fetchNudge = () =>
            api
                .get("/nudge-queue/summary")
                .then((r) => {
                    if (cancel) return;
                    const d = r.data || {};
                    setNudgeCount(d.badge_count || 0);
                    const critical = d.awaiting_registration?.critical || 0;
                    setNudgeUrgent(critical > 0);
                })
                .catch(() => {});
        fetchNudge();
        const t = setInterval(fetchNudge, 90000);
        return () => { cancel = true; clearInterval(t); };
    }, [user]);

    // ⌘K / Ctrl+K global command palette
    const [paletteOpen, setPaletteOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const { isEnabled } = useFeatures();

    // V1.5.8.26 — workspace-wide sidebar order (admin-managed via Settings).
    // Fetch once per user session; refresh when the saved event fires.
    const [navOrder, setNavOrder] = useState([]);
    useEffect(() => {
        if (!user || user === false) return;
        if (!["admin", "recruiter", "hiring_manager"].includes(user.role)) return;
        let cancel = false;
        const load = () =>
            api.get("/workspace/nav-order")
                .then((r) => { if (!cancel) setNavOrder(r.data?.order || []); })
                .catch(() => {});
        load();
        const h = () => load();
        window.addEventListener("naukri:nav-order-changed", h);
        return () => { cancel = true; window.removeEventListener("naukri:nav-order-changed", h); };
    }, [user]);

    const visibleNav = useMemo(() => {
        const role = user?.role || "recruiter";
        const filtered = NAV.filter((n) => {
            if (!n.roles.includes(role)) return false;
            // Feature-gated nav items are hidden when the tenant's plan doesn't include them.
            // Superadmin always sees everything (isEnabled returns true for all).
            if (n.feature && !isEnabled(n.feature)) return false;
            return true;
        });
        if (!navOrder || navOrder.length === 0) return filtered;
        // Sort by saved order; items not in the saved order keep their
        // hardcoded position at the end (preserves relative order).
        const idx = new Map(navOrder.map((path, i) => [path, i]));
        return [...filtered].sort((a, b) => {
            const ai = idx.has(a.to) ? idx.get(a.to) : 10_000;
            const bi = idx.has(b.to) ? idx.get(b.to) : 10_000;
            return ai - bi;
        });
    }, [user?.role, isEnabled, navOrder]);

    const SidebarContent = (
        <>
            <div className="p-5 lg:p-6 border-b border-[#E5E5E5] flex items-center justify-between">
                <Brand size="h-8" logoUrl={tenant?.logo_url} name={tenant?.name} role={user?.role} />
                <button
                    className="lg:hidden p-1 -mr-1"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-sidebar-close"
                    aria-label="Close menu"
                >
                    <X size={20} weight="bold" />
                </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {visibleNav.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={n.testid}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-sm font-bold transition-colors ${
                                isActive
                                    ? "bg-ink text-white"
                                    : "text-[#525252] hover:bg-[#F5F5F5] hover:text-ink"
                            }`
                        }
                    >
                        <n.icon size={18} weight="bold" />
                        <span className="flex-1">{n.label}</span>
                        {n.badge && badgeCount > 0 && (
                            <span
                                data-testid={`${n.testid}-badge`}
                                title={badgeUrgent ? `${overdueCount} overdue` : `${followupCount} pending`}
                                className={`text-[10px] font-black text-white px-1.5 py-0.5 min-w-[18px] text-center leading-tight ${
                                    badgeUrgent ? "bg-[#DC2626]" : "bg-[#525252]"
                                }`}
                            >
                                {badgeCount > 99 ? "99+" : badgeCount}
                            </span>
                        )}
                        {n.nudgeBadge && nudgeCount > 0 && (
                            <span
                                data-testid={`${n.testid}-badge`}
                                title={nudgeUrgent ? "Awaiting final reminder" : "Pending nudges"}
                                className={`text-[10px] font-black text-white px-1.5 py-0.5 min-w-[18px] text-center leading-tight ${
                                    nudgeUrgent ? "bg-[#DC2626]" : "bg-cobalt"
                                }`}
                            >
                                {nudgeCount > 99 ? "99+" : nudgeCount}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="p-3 border-t border-[#E5E5E5] shrink-0">
                {["admin", "recruiter", "hiring_manager"].includes(user?.role) && (
                    <div className="mb-3">
                        <AvailableNowToggle />
                    </div>
                )}
                <div className="px-3 py-2 flex items-center gap-3">
                    <NavLink
                        to="/settings/profile"
                        data-testid="sidebar-profile-avatar-link"
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-cobalt to-cobalt/70 text-white font-black text-sm flex items-center justify-center overflow-hidden ring-2 ring-white shrink-0 relative group"
                        title="Edit profile photo"
                    >
                        {user?.avatar_url ? (
                            <img src={user.avatar_url} alt={user?.name || "Me"} className="w-full h-full object-cover" />
                        ) : (
                            <span>{(user?.name || "?").trim().charAt(0).toUpperCase()}</span>
                        )}
                        <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-[9px] font-bold">
                            Edit
                        </span>
                    </NavLink>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Signed in</p>
                        <p className="text-sm font-bold text-ink truncate" data-testid="current-user-name">
                            {user?.name}
                        </p>
                        <p className="text-xs text-[#525252] truncate" data-testid="current-user-email">
                            {user?.email}
                        </p>
                    </div>
                </div>
                <div className="px-3">
                    {user?.role && (
                        <span
                            data-testid="current-user-role"
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]"
                        >
                            <ShieldCheck size={9} weight="fill" /> {user.role.replace("_", " ")}
                        </span>
                    )}
                </div>
                <NavLink
                    to="/settings/sessions"
                    data-testid="sidebar-sessions-link"
                    className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-sm font-bold text-[#525252] hover:text-ink hover:bg-[#F5F5F5] transition-colors"
                >
                    <ShieldCheck size={16} weight="bold" /> Signed-in devices
                </NavLink>
                <button
                    onClick={logout}
                    data-testid="logout-button"
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-bold text-[#525252] hover:text-ink hover:bg-[#F5F5F5] transition-colors"
                >
                    <SignOut size={16} weight="bold" /> Logout
                </button>
                <div className="mt-2">
                    <InstallAppButton
                        label="Install app"
                        testId="recruiter-sidebar-install-app"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-black uppercase tracking-wider border border-cobalt text-cobalt hover:bg-cobalt hover:text-white transition-colors rounded-md"
                    />
                </div>
            </div>
        </>
    );

    return (
        <div
            className="h-screen flex bg-paper"
            data-testid="app-shell"
            data-tenant-primary={tenant?.primary_color || undefined}
            style={tenant?.primary_color ? { "--tenant-primary": tenant.primary_color } : undefined}
        >
            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#E5E5E5] h-topbar-safe safe-top flex items-center justify-between px-4">
                <Brand size="h-7" logoUrl={tenant?.logo_url} name={tenant?.name} role={user?.role} />
                <div className="flex items-center gap-2">
                    {user && ["admin", "recruiter", "hiring_manager"].includes(user.role) && (
                        <button
                            onClick={() => nav("/notifications")}
                            data-testid="mobile-notification-bell"
                            aria-label="Notifications"
                            className="relative p-2 -mr-1"
                        >
                            <Bell size={20} weight="bold" />
                            {badgeCount > 0 && (
                                <span
                                    data-testid="mobile-notification-badge"
                                    className={`absolute top-0 right-0 text-[9px] font-black text-white px-1 min-w-[16px] leading-tight ${
                                        badgeUrgent ? "bg-[#DC2626]" : "bg-[#525252]"
                                    }`}
                                >
                                    {badgeCount > 99 ? "99+" : badgeCount}
                                </span>
                            )}
                        </button>
                    )}
                    <button
                        onClick={() => setOpen(true)}
                        data-testid="mobile-menu-button"
                        className="p-2 -mr-2"
                        aria-label="Open menu"
                    >
                        <List size={22} weight="bold" />
                    </button>
                </div>
            </div>

            {/* Desktop sidebar */}
            <aside className="hidden lg:flex w-[240px] shrink-0 bg-white border-r border-[#E5E5E5] flex-col">
                {SidebarContent}
            </aside>

            {/* Mobile drawer */}
            {open && (
                <>
                    <div
                        className="lg:hidden fixed inset-0 bg-ink/40 backdrop-blur-sm z-40"
                        onClick={() => setOpen(false)}
                    />
                    <aside className="lg:hidden fixed top-0 bottom-0 left-0 w-[260px] bg-white border-r border-[#E5E5E5] flex flex-col z-50 safe-top">
                        {SidebarContent}
                    </aside>
                </>
            )}

            {/* Main */}
            {/* V2.0.25 — `overflow-y-auto` restores vertical scroll for
                long non-chat pages (JobDetail, Dashboard, Kanban, etc.)
                that V2.0.11 accidentally clipped when it flexed the
                whole app-shell to a strict `h-screen`. Chat pages
                already use `.h-chat-body` + `overflow-hidden` on their
                own roots so they scroll internally and this outer scroll
                is a no-op for them. */}
            <main className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto pt-topbar-safe lg:pt-0 flex flex-col">
                <ImpersonationBanner />
                <PageTransition />
            </main>

            {/* Global ⌘K palette */}
            {user && ["admin", "recruiter", "hiring_manager"].includes(user.role) && (
                <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
            )}

            {/* Desktop-only floating ⌘K hint (bottom-left, above "Made with Emergent" badge) */}
            {user && ["admin", "recruiter", "hiring_manager"].includes(user.role) && (
                <button
                    type="button"
                    onClick={() => setPaletteOpen(true)}
                    data-testid="command-palette-trigger"
                    aria-label="Open command palette"
                    className="hidden lg:flex fixed bottom-5 left-[260px] z-30 items-center gap-2 bg-white border border-[#E5E5E5] hover:border-ink text-[#525252] hover:text-ink px-3 py-2 shadow-sm transition-colors text-xs font-bold"
                >
                    <span className="uppercase tracking-wider">Search</span>
                    <kbd className="font-mono text-[10px] font-black bg-[#F5F5F5] border border-[#E5E5E5] px-1.5 py-0.5">
                        ⌘K
                    </kbd>
                </button>
            )}
        </div>
    );
}
